/**
 * Image processing utilities for thumbnail generation and image manipulation
 */

export interface ThumbnailOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'jpeg' | 'png' | 'webp';
  maintainAspectRatio?: boolean;
}

export interface ThumbnailResult {
  blob: Blob;
  url: string;
  width: number;
  height: number;
}

/**
 * Generate a thumbnail from a File or Blob
 */
export function generateThumbnail(
  file: File | Blob,
  options: ThumbnailOptions = {}
): Promise<ThumbnailResult> {
  const {
    width = 400,
    height = 400,
    quality = 1,
    format = 'jpeg',
    maintainAspectRatio = true,
  } = options;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Canvas 2D context not supported'));
      return;
    }

    const img = new Image();
    
    img.onload = () => {
      try {
        let targetWidth = width;
        let targetHeight = height;

        // Calculate dimensions maintaining aspect ratio if requested
        if (maintainAspectRatio) {
          const aspectRatio = img.width / img.height;
          if (width / height > aspectRatio) {
            targetWidth = height * aspectRatio;
            targetHeight = height;
          } else {
            targetWidth = width;
            targetHeight = width / aspectRatio;
          }
        }

        // Set canvas dimensions
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Clear canvas and draw image
        ctx.clearRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              resolve({
                blob,
                url,
                width: targetWidth,
                height: targetHeight,
              });
            } else {
              reject(new Error('Failed to generate thumbnail blob'));
            }
          },
          `image/${format}`,
          quality
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Create object URL for the image
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;
  });
}

/**
 * Generate multiple thumbnails from files
 */
export function generateMultipleThumbnails(
  files: File[],
  options: ThumbnailOptions = {}
): Promise<ThumbnailResult[]> {
  const promises = files.map(file => generateThumbnail(file, options));
  return Promise.all(promises);
}

/**
 * Create a preview URL for a file (for immediate display)
 */
export function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Clean up preview URLs to prevent memory leaks
 */
export function revokePreviewUrl(url: string): void {
  URL.revokeObjectURL(url);
}

/**
 * Get image dimensions from a file
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Format file size in human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Validate if file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
}

/**
 * Generate thumbnail filename from original filename
 */
export function generateThumbnailFilename(originalFilename: string, format: string = 'jpg'): string {
  const nameWithoutExt = originalFilename.replace(/\.[^/.]+$/, '');
  return `thumb_${nameWithoutExt}.${format}`;
}

/**
 * Generate thumbnail key for storage (S3 path)
 */
export function generateThumbnailKey(originalKey: string, format: string = 'jpg'): string {
  const pathParts = originalKey.split('/');
  const filename = pathParts.pop();
  if (!filename) return originalKey;
  
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
  const thumbnailFilename = `thumb_${nameWithoutExt}.${format}`;
  
  return [...pathParts, thumbnailFilename].join('/');
}
